import { AgreementDTO, AgreementItemPropertyTypes } from 'models'
import { DiffMapResult, ObjectValueDiff, ObjectValueStatus } from './AgreementPageStore.types'

export class DeepDiffMapper {
  protected isFunction (obj: object): boolean {
    return {}.toString.apply(obj) === '[object Function]'
  }

  protected isArray (obj: object): boolean {
    return {}.toString.apply(obj) === '[object Array]'
  }

  protected isObject (obj: object): boolean {
    return {}.toString.apply(obj) === '[object Object]'
  }

  protected isDate (obj: object): boolean {
    return {}.toString.apply(obj) === '[object Date]'
  }

  protected isValue (obj: object): boolean {
    return !this.isObject(obj) && !this.isArray(obj)
  }

  protected compareValues (value1: any, value2: any) {
    if (value1 === value2) {
      return ObjectValueStatus.UNCHANGED
    }
    if (this.isDate(value1) && this.isDate(value2) && value1.getTime() === value2.getTime()) {
      return ObjectValueStatus.UNCHANGED
    }
    if (typeof (value1) === 'undefined') {
      return ObjectValueStatus.ADDED
    }
    if (typeof (value2) === 'undefined') {
      return ObjectValueStatus.REMOVED
    }

    return ObjectValueStatus.CHANGED
  }

  public map<T> (obj1: object, obj2: object): DiffMapResult<T> {
    if (this.isFunction(obj1) || this.isFunction(obj2)) {
      console.error('Invalid argument. Function given, object expected.')
      return
    }
    if (this.isValue(obj1) || this.isValue(obj2)) {
      const returnObj: ObjectValueDiff = {
        status: this.compareValues(obj1, obj2),
        // data: (obj1 === undefined) ? obj2 : obj1,
        beforeChange: obj1,
        afterChange: obj2
      }

      if (returnObj.status !== ObjectValueStatus.UNCHANGED) {
        return returnObj
      }
      return undefined
    }

    const diff = {}
    for (const key in obj1) {
      if (this.isFunction(obj1[key])) {
        continue
      }

      let value2
      if (typeof (obj2[key]) !== 'undefined') {
        value2 = obj2[key]
      }

      diff[key] = this.map(obj1[key], value2)
    }
    for (const key in obj2) {
      if (this.isFunction(obj2[key]) || (typeof (diff[key]) !== 'undefined')) {
        continue
      }

      diff[key] = this.map(undefined, obj2[key])
    }

    return diff as any
  }
}

export const getAgreementPropLabel = (
  key: keyof AgreementDTO | AgreementItemPropertyTypes
): { label: string, sectionLabel?: string, subSectionLabel?: string, unit?: string } => {
  switch (key) {
    case 'agreementStatus': {
      return {
        label: 'Status'
      }
    }
    case 'name': {
      return {
        label: 'name'
      }
    }
    case 'budget': {
      return {
        label: 'budget',
        unit: 'USD'
      }
    }
    case 'budgetBuffer': {
      return {
        label: 'buffer',
        unit: '%'
      }
    }
    case 'startDate':
    case 'endDate': {
      return {
        label: 'period',
        sectionLabel: 'Period'
      }
    }
    case AgreementItemPropertyTypes.BLOB_TRIGGERED: {
      return {
        label: 'price per one trigger',
        sectionLabel: 'Blob',
        unit: 'USD'
      }
    }
    case AgreementItemPropertyTypes.MAX_ATTACH_TIME_SECONDS: {
      return {
        label: 'max attach time',
        sectionLabel: 'Blob',
        unit: 'seconds'
      }
    }
    case AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_ATTACHED: {
      return {
        label: 'price per time on screen attached',
        sectionLabel: 'Blob',
        subSectionLabel: 'price per time on screen',
        unit: 'USD'
      }
    }
    case AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_DETACHED: {
      return {
        label: 'price per time on screen detached',
        sectionLabel: 'Blob',
        subSectionLabel: 'Price per time on screen',
        unit: 'USD'
      }
    }
    case AgreementItemPropertyTypes.MINIMUM_TIME_IN_STACK: {
      return {
        label: 'minimum time in stack',
        sectionLabel: 'Blob',
        subSectionLabel: 'Time in stack',
        unit: 'seconds'
      }
    }
    case AgreementItemPropertyTypes.MAXIMUM_TIME_IN_STACK: {
      return {
        label: 'maximum time in stack',
        sectionLabel: 'Blob',
        subSectionLabel: 'Time in stack',
        unit: 'seconds'
      }
    }
    case AgreementItemPropertyTypes.PRODUCT_PAGE_OPENED_FROM_BLOB: {
      return {
        label: 'price per opened product page',
        sectionLabel: 'Product',
        unit: 'USD'
      }
    }
    case AgreementItemPropertyTypes.PRODUCT_ADDED_TO_CART: {
      return {
        label: 'price per product added to cart',
        sectionLabel: 'Product',
        unit: 'USD'
      }
    }
    case AgreementItemPropertyTypes.PRICE_PER_VIDEO_PLAYBACK: {
      return {
        label: 'price per video playback',
        sectionLabel: 'Video commercial',
        unit: 'USD'
      }
    }
    case AgreementItemPropertyTypes.PRICE_PER_CLICK: {
      return {
        label: 'price per poster click',
        sectionLabel: 'Poster',
        unit: 'USD'
      }
    }
    case AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN: {
      return {
        label: 'price per second poster on screen',
        sectionLabel: 'Poster',
        unit: 'USD'
      }
    }
    case AgreementItemPropertyTypes.PURCHASE_FEE: {
      return {
        label: 'product purchase fee',
        sectionLabel: 'Product purchase',
        unit: 'USD'
      }
    }
  }
}
